import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  LinearProgress,
  MenuItem,
  Select,
  Snackbar,
  TextField,
  Typography
} from '@mui/material';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import { FormattedMessage, useIntl } from 'react-intl';
import axiosConfig from 'src/Resources/axiosConfig';
import CommentFormatToolbar from './CommentFormatToolbar';
import { handleListEnter } from './commentMarkdown';
import { usePasteImage, PendingPaste } from 'src/hooks/usePasteImage';
import {
  TicketPriority,
  TicketCategory,
  TicketSubCategory,
  subCategoryLabels,
  subCategoriesByCategory,
  categoryLabels,
  otherSubCategoryValues,
  AdminUser
} from 'src/interfaces/TicketTypes';

type Installation = {
  id: number;
  name: string;
  device: number;
};

const productOptions = [
  { value: 0, label: 'Salimax' },
  { value: 1, label: 'Salidomo' },
  { value: 2, label: 'Sodistore Home' },
  { value: 3, label: 'Sodistore Max' },
  { value: 4, label: 'Sodistore Grid' },
  { value: 5, label: 'Sodistore Pro' }
];

const deviceOptionsByProduct: Record<number, { value: number; label: string }[]> = {
  1: [
    { value: 1, label: 'Cerbo' },
    { value: 2, label: 'Venus' }
  ],
  2: [
    { value: 3, label: 'Growatt' },
    { value: 4, label: 'inesco 12K - WR Hybrid' }
  ]
};

interface Props {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
  defaultInstallationId?: number;
}

function CreateTicketModal({ open, onClose, onCreated, defaultInstallationId }: Props) {
  const intl = useIntl();
  const [subject, setSubject] = useState('');
  const [selectedProduct, setSelectedProduct] = useState<number | ''>('');
  const [selectedDevice, setSelectedDevice] = useState<number | ''>('');
  const [allInstallations, setAllInstallations] = useState<Installation[]>([]);
  const [selectedInstallation, setSelectedInstallation] =
    useState<Installation | null>(null);
  const [loadingInstallations, setLoadingInstallations] = useState(false);
  const [priority, setPriority] = useState<number>(TicketPriority.Medium);
  const [assigneeId, setAssigneeId] = useState<number | ''>('');
  const [adminUsers, setAdminUsers] = useState<AdminUser[]>([]);
  const [category, setCategory] = useState<number>(TicketCategory.Hardware);
  const [subCategory, setSubCategory] = useState<number>(
    TicketSubCategory.Battery
  );
  const [description, setDescription] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // File attachments
  const fileInputRef = useRef<HTMLInputElement>(null);
  const descriptionRef = useRef<HTMLInputElement | null>(null);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  const [pendingPastes, setPendingPastes] = useState<PendingPaste[]>([]);
  const [pasteError, setPasteError] = useState<'tooLarge' | 'uploadFailed' | null>(null);

  const pendingPastesRef = useRef<PendingPaste[]>([]);
  pendingPastesRef.current = pendingPastes;

  useEffect(() => {
    return () => {
      pendingPastesRef.current.forEach((p) => window.URL.revokeObjectURL(p.blobUrl));
    };
  }, []);

  const handlePasteDescription = usePasteImage({
    mode: 'deferred',
    textareaRef: descriptionRef,
    value: description,
    onChange: setDescription,
    onError: setPasteError,
    onBufferedPaste: (p) => setPendingPastes((prev) => [...prev, p])
  });

  const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'application/pdf'];
  const MAX_FILE_SIZE = 25 * 1024 * 1024;

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      if (!ALLOWED_TYPES.includes(file.type)) {
        setError(`Invalid file type: ${file.name}`);
        return;
      }
      if (file.size > MAX_FILE_SIZE) {
        setError(`File too large: ${file.name} (max 25 MB)`);
        return;
      }
    }
    setError('');
    setSelectedFiles((prev) => [...prev, ...Array.from(files)]);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const removeFile = (index: number) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  // Custom "Other" fields
  const [customSubCategory, setCustomSubCategory] = useState('');
  const [customCategory, setCustomCategory] = useState('');
  const [customSubSuggestions, setCustomSubSuggestions] = useState<string[]>([]);
  const [customCatSuggestions, setCustomCatSuggestions] = useState<string[]>([]);

  const hasDeviceOptions =
    selectedProduct !== '' && selectedProduct in deviceOptionsByProduct;

  const isOtherCategory = category === TicketCategory.Other;
  const isOtherSubCategory = otherSubCategoryValues.has(subCategory);

  // Fetch custom subcategory suggestions when category changes
  useEffect(() => {
    if (!isOtherSubCategory && !isOtherCategory) {
      setCustomSubSuggestions([]);
      return;
    }
    axiosConfig
      .get('/GetCustomSubCategories', { params: { category } })
      .then((res) => {
        if (Array.isArray(res.data)) setCustomSubSuggestions(res.data);
      })
      .catch(() => setCustomSubSuggestions([]));
  }, [category, isOtherSubCategory, isOtherCategory]);

  // Fetch custom category suggestions when "Other" category is selected
  useEffect(() => {
    if (!isOtherCategory) {
      setCustomCatSuggestions([]);
      return;
    }
    axiosConfig
      .get('/GetCustomCategories')
      .then((res) => {
        if (Array.isArray(res.data)) setCustomCatSuggestions(res.data);
      })
      .catch(() => setCustomCatSuggestions([]));
  }, [isOtherCategory]);

  useEffect(() => {
    if (selectedProduct === '') {
      setAllInstallations([]);
      setSelectedInstallation(null);
      setSelectedDevice('');
      return;
    }
    setLoadingInstallations(true);
    setSelectedInstallation(null);
    setSelectedDevice('');
    axiosConfig
      .get('/GetAllInstallationsFromProduct', {
        params: { product: selectedProduct }
      })
      .then((res) => {
        const data = res.data;
        if (Array.isArray(data)) {
          const mapped = data.map((item: any) => ({
            id: item.id,
            name: item.name,
            device: item.device
          }));
          setAllInstallations(mapped);
          if (defaultInstallationId != null) {
            const match = mapped.find((inst: Installation) => inst.id === defaultInstallationId);
            if (match) {
              setSelectedInstallation(match);
              setSelectedDevice(match.device ?? '');
            }
          }
        }
      })
      .catch(() => setAllInstallations([]))
      .finally(() => setLoadingInstallations(false));
  }, [selectedProduct]);

  useEffect(() => {
    if (!open) return;
    axiosConfig
      .get('/GetAdminUsers')
      .then((res) => {
        if (Array.isArray(res.data)) setAdminUsers(res.data);
      })
      .catch(() => setAdminUsers([]));
  }, [open]);

  useEffect(() => {
    if (defaultInstallationId == null || !open) return;
    axiosConfig
      .get('/GetInstallationById', { params: { id: defaultInstallationId } })
      .then((res) => {
        const inst = res.data;
        if (inst) {
          setSelectedProduct(inst.product);
        }
      })
      .catch(() => {});
  }, [defaultInstallationId, open]);

  useEffect(() => {
    if (defaultInstallationId == null) setSelectedInstallation(null);
  }, [selectedDevice]);

  // Reset subcategory when category changes
  useEffect(() => {
    if (isOtherCategory) {
      setSubCategory(0); // no subcategory for Other category
    } else {
      const subs = subCategoriesByCategory[category];
      setSubCategory(subs?.[0] ?? 0);
    }
    setCustomSubCategory('');
    setCustomCategory('');
  }, [category]);

  const filteredInstallations = useMemo(() => {
    if (!hasDeviceOptions || selectedDevice === '') return allInstallations;
    return allInstallations.filter((inst) => inst.device === selectedDevice);
  }, [allInstallations, selectedDevice, hasDeviceOptions]);

  const installationReady =
    selectedProduct !== '' && (!hasDeviceOptions || selectedDevice !== '');

  const resetForm = () => {
    setSubject('');
    setSelectedProduct('');
    setSelectedDevice('');
    setAllInstallations([]);
    setSelectedInstallation(null);
    setPriority(TicketPriority.Medium);
    setAssigneeId('');
    setCategory(TicketCategory.Hardware);
    setSubCategory(TicketSubCategory.Battery);
    setDescription('');
    setCustomSubCategory('');
    setCustomCategory('');
    setSelectedFiles([]);
    pendingPastes.forEach((p) => window.URL.revokeObjectURL(p.blobUrl));
    setPendingPastes([]);
    setError('');
  };

  const handleSubmit = async () => {
    if (!subject.trim()) return;
    if (assigneeId === '') {
      setError(
        intl.formatMessage({
          id: 'assigneeRequired',
          defaultMessage: 'Please assign this ticket to someone before creating it.'
        })
      );
      return;
    }
    setSubmitting(true);
    setError('');

    try {
      const res = await axiosConfig.post('/CreateTicket', {
        subject,
        description,
        installationId: selectedInstallation?.id ?? null,
        priority,
        assigneeId,
        category,
        subCategory: isOtherCategory ? 0 : subCategory,
        customSubCategory: (isOtherSubCategory || isOtherCategory) ? customSubCategory || null : null,
        customCategory: isOtherCategory ? customCategory || null : null
      });

      const createdTicket = res.data;
      const newTicketId = createdTicket?.id;

      // Upload buffered pasted images, then rewrite blob: URLs in description
      if (pendingPastes.length > 0 && newTicketId) {
        setUploading(true);
        let finalDescription = description;
        let descChanged = false;
        for (const paste of pendingPastes) {
          const formData = new FormData();
          formData.append('file', paste.blob);
          try {
            const upRes = await axiosConfig.post('/UploadDocument', formData, {
              params: { scope: 0, ticketId: newTicketId },
              headers: { 'Content-Type': 'multipart/form-data' }
            });
            const docId = upRes.data?.id;
            if (docId && finalDescription.includes(paste.blobUrl)) {
              finalDescription = finalDescription.split(paste.blobUrl).join(`/DownloadDocument?id=${docId}`);
              descChanged = true;
            }
          } catch (err: any) {
            console.warn('[Paste] Upload failed:', err?.response?.data || err?.message);
            setPasteError('uploadFailed');
          }
          window.URL.revokeObjectURL(paste.blobUrl);
        }
        if (descChanged && createdTicket) {
          try {
            await axiosConfig.put('/UpdateTicket', {
              ...createdTicket,
              description: finalDescription
            });
          } catch (err: any) {
            console.warn('[Paste] Description rewrite failed:', err?.response?.data || err?.message);
          }
        }
        setUploading(false);
      }

      // Upload attached files if any
      if (selectedFiles.length > 0 && newTicketId) {
        setUploading(true);
        for (const file of selectedFiles) {
          const formData = new FormData();
          formData.append('file', file);
          try {
            await axiosConfig.post('/UploadDocument', formData, {
              params: { scope: 0, ticketId: newTicketId },
              headers: { 'Content-Type': 'multipart/form-data' },
              onUploadProgress: (e) => {
                if (e.total) setUploadProgress(Math.round((e.loaded * 100) / e.total));
              }
            });
          } catch (err: any) {
            console.warn(`[Documents] Upload failed for ${file.name}:`, err?.response?.data || err?.message);
          }
        }
        setUploading(false);
        setUploadProgress(0);
      }

      resetForm();
      onCreated();
      onClose();
    } catch {
      setError('Failed to create ticket.');
    } finally {
      setSubmitting(false);
    }
  };

  const availableSubCategories = subCategoriesByCategory[category] ?? [];

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <FormattedMessage id="createTicket" defaultMessage="Create Ticket" />
      </DialogTitle>
      <DialogContent
        sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}
      >
        {error && <Alert severity="error">{error}</Alert>}

        <TextField
          label={<FormattedMessage id="subject" defaultMessage="Subject" />}
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          required
          fullWidth
          margin="dense"
        />

        <FormControl fullWidth margin="dense">
          <InputLabel>
            <FormattedMessage id="product" defaultMessage="Product" />
          </InputLabel>
          <Select
            value={selectedProduct}
            label="Product"
            onChange={(e) =>
              setSelectedProduct(
                e.target.value === '' ? '' : Number(e.target.value)
              )
            }
          >
            {productOptions.map((p) => (
              <MenuItem key={p.value} value={p.value}>
                {p.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {hasDeviceOptions && (
          <FormControl fullWidth margin="dense">
            <InputLabel>
              <FormattedMessage id="device" defaultMessage="Device" />
            </InputLabel>
            <Select
              value={selectedDevice}
              label="Device"
              onChange={(e) =>
                setSelectedDevice(
                  e.target.value === '' ? '' : Number(e.target.value)
                )
              }
            >
              {deviceOptionsByProduct[selectedProduct as number].map((d) => (
                <MenuItem key={d.value} value={d.value}>
                  {d.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        <Autocomplete<Installation, false, false, false>
          options={filteredInstallations}
          getOptionLabel={(opt) => opt.name}
          value={selectedInstallation}
          onChange={(_e, val) => setSelectedInstallation(val)}
          disabled={!installationReady}
          loading={loadingInstallations}
          renderInput={(params) => (
            <TextField
              {...params}
              label={
                <FormattedMessage
                  id="installation"
                  defaultMessage="Installation"
                />
              }
              margin="dense"
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {loadingInstallations ? (
                      <CircularProgress size={20} />
                    ) : null}
                    {params.InputProps.endAdornment}
                  </>
                )
              }}
            />
          )}
        />

        <FormControl fullWidth margin="dense" required error={assigneeId === ''}>
          <InputLabel>
            <FormattedMessage id="assignee" defaultMessage="Assignee" />
          </InputLabel>
          <Select
            value={assigneeId}
            label="Assignee"
            onChange={(e) =>
              setAssigneeId(e.target.value === '' ? '' : Number(e.target.value))
            }
          >
            {adminUsers
              .filter((u) => {
                const name = (u.name ?? '').toLowerCase();
                return (
                  !name.includes('inesco energy master admin') &&
                  !name.includes('paal myhre')
                );
              })
              .map((u) => (
                <MenuItem key={u.id} value={u.id}>
                  {u.name}
                </MenuItem>
              ))}
          </Select>
        </FormControl>

        <FormControl fullWidth margin="dense">
          <InputLabel>
            <FormattedMessage id="priority" defaultMessage="Priority" />
          </InputLabel>
          <Select
            value={priority}
            label="Priority"
            onChange={(e) => setPriority(Number(e.target.value))}
          >
            <MenuItem value={TicketPriority.Critical}>Critical</MenuItem>
            <MenuItem value={TicketPriority.High}>High</MenuItem>
            <MenuItem value={TicketPriority.Medium}>Medium</MenuItem>
            <MenuItem value={TicketPriority.Low}>Low</MenuItem>
          </Select>
        </FormControl>

        <FormControl fullWidth margin="dense">
          <InputLabel>
            <FormattedMessage id="category" defaultMessage="Category" />
          </InputLabel>
          <Select
            value={category}
            label="Category"
            onChange={(e) => setCategory(Number(e.target.value))}
          >
            {Object.entries(categoryLabels).map(([val, label]) => (
              <MenuItem key={val} value={Number(val)}>
                {label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Custom category label when "Other" category is selected */}
        {isOtherCategory && (
          <Autocomplete<string, false, false, true>
            freeSolo
            options={customCatSuggestions}
            value={customCategory}
            onInputChange={(_e, val) => setCustomCategory(val)}
            renderInput={(params) => (
              <TextField
                {...params}
                label={
                  <FormattedMessage
                    id="customCategoryLabel"
                    defaultMessage="Category Label"
                  />
                }
                placeholder="Type or select from existing..."
                margin="dense"
              />
            )}
          />
        )}

        {/* Subcategory dropdown — hidden when category is "Other" */}
        {!isOtherCategory && availableSubCategories.length > 0 && (
          <FormControl fullWidth margin="dense">
            <InputLabel>
              <FormattedMessage
                id="subCategory"
                defaultMessage="Sub-Category"
              />
            </InputLabel>
            <Select
              value={subCategory}
              label="Sub-Category"
              onChange={(e) => {
                setSubCategory(Number(e.target.value));
                setCustomSubCategory('');
              }}
            >
              {availableSubCategories.map((val) => (
                <MenuItem key={val} value={val}>
                  {subCategoryLabels[val] ?? 'Unknown'}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        {/* Custom subcategory free-text with autocomplete when "Other" sub is selected */}
        {(isOtherSubCategory || isOtherCategory) && (
          <Autocomplete<string, false, false, true>
            freeSolo
            options={customSubSuggestions}
            value={customSubCategory}
            onInputChange={(_e, val) => setCustomSubCategory(val)}
            renderInput={(params) => (
              <TextField
                {...params}
                label={
                  <FormattedMessage
                    id="customSubCategoryLabel"
                    defaultMessage="Custom Sub-Category"
                  />
                }
                placeholder="Type or select from existing..."
                margin="dense"
              />
            )}
          />
        )}

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 1 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
            <CommentFormatToolbar
              textareaRef={descriptionRef}
              value={description}
              onChange={setDescription}
              disabled={submitting || uploading}
            />
            <Typography variant="caption" color="text.disabled">
              <FormattedMessage
                id="commentMarkdownHint"
                defaultMessage="Markdown: **bold**, #, ##"
              />
            </Typography>
          </Box>
          <TextField
            label={
              <FormattedMessage id="description" defaultMessage="Description" />
            }
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            onKeyDown={(e) => handleListEnter(e, description, setDescription)}
            onPaste={handlePasteDescription}
            inputRef={descriptionRef}
            multiline
            rows={4}
            fullWidth
            margin="dense"
          />
        </Box>

        {/* File attachments */}
        <Box sx={{ mt: 1 }}>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/gif,image/webp,application/pdf"
            multiple
            style={{ display: 'none' }}
            onChange={handleFileSelect}
          />
          <Button
            variant="outlined"
            size="small"
            startIcon={<AttachFileIcon />}
            onClick={() => fileInputRef.current?.click()}
            disabled={submitting || uploading}
          >
            <FormattedMessage id="attachFiles" defaultMessage="Attach Files" />
          </Button>
          {selectedFiles.length > 0 && (
            <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {selectedFiles.map((f, i) => (
                <Chip
                  key={i}
                  label={f.name}
                  size="small"
                  onDelete={() => removeFile(i)}
                />
              ))}
            </Box>
          )}
          {uploading && (
            <Box sx={{ mt: 1 }}>
              <LinearProgress variant="determinate" value={uploadProgress} />
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>
          <FormattedMessage id="cancel" defaultMessage="Cancel" />
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={submitting || !subject.trim()}
        >
          <FormattedMessage id="submit" defaultMessage="Submit" />
        </Button>
      </DialogActions>
      <Snackbar
        open={pasteError !== null}
        autoHideDuration={4000}
        onClose={() => setPasteError(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="error" onClose={() => setPasteError(null)}>
          {pasteError === 'tooLarge' ? (
            <FormattedMessage
              id="pasteImageTooLarge"
              defaultMessage="Pasted image exceeds 100 MB limit"
            />
          ) : (
            <FormattedMessage
              id="pasteImageUploadFailed"
              defaultMessage="Failed to upload pasted image"
            />
          )}
        </Alert>
      </Snackbar>
    </Dialog>
  );
}

export default CreateTicketModal;
